import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  FormGroup,
  FormControlLabel,
  DialogTitle,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AgGridReact } from "ag-grid-react";
import PaginationSizeSelector from "../../trips/columnDefComponents/PaginationSizeSelector";
import { IOSSwitch } from "./IOSSwitch";
import { Block, CheckOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { formatDate, formatDateGGMMAAAA, formatDateTime } from "../../../utils/DateUtils";
import RegisteredVehicleStatusFilter from "./RegisteredVehicleStatusFilter";

interface DialogComponentProps {
  open: boolean;
  onClose: () => void;
  vehiclesGridData: any;
  handleStatusVehicleChange: (licensePlate: string) => Promise<void>;
  handleOpenViewSidebar: (data:any) => void;
}

const DialogComponent: React.FC<DialogComponentProps> = ({
  open,
  onClose,
  handleStatusVehicleChange,
  vehiclesGridData,
  handleOpenViewSidebar
}) => {
  const { t } = useTranslation();
  
  const [gridData,setGridData] = useState<any>(vehiclesGridData?.vehicles )
  const columnDefsRegistered = useMemo(
    () => [
      {
        headerName: t("screens.fleetAndRoute.gridGroupedVehicles.plate"),
        field: "licensePlate",
        headerTooltip: t("screens.fleetAndRoute.gridGroupedVehicles.plate").toString(),
        tooltipValueGetter: (params: any): string => {
          return params.data.licensePlate || "-";
        },
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ['contains'],
          buttons: [],
          closeOnApply: false,
          suppressAndOrCondition: true,
        },
        floatingFilter: true,
        cellRenderer: (params: any) => {
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                alignItems: "center",
                height: "40px",
                
              }}
            >
              <Button
              onClick={() => handleOpenViewSidebar(params.data)}
                variant="text"
                sx={{ color: "black", padding: 0, margin: 0, textDecoration:'underline' }}
              >
                {params.data.licensePlate}
              </Button>
            </Box>
          );
        },
      },

       {
             headerName: t("screens.fleetAndRoute.gridAssets.firstRegistrationYear"),
             field: "firstRegistrationDate",
             filter: "agDateColumnFilter",
             valueGetter: (params: any) => {
               if (!params.data?.firstRegistrationDate) return null;
               const date = new Date(params.data.firstRegistrationDate);
               return isNaN(date.getTime()) ? null : date;
             },
             filterParams: {
               maxNumConditions: 1,
               filterOptions: ['inRange'],
               comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
                 if (!cellValue) return -1;
                 const startOfDay = new Date(filterLocalDateAtMidnight);
                 const endOfDay = new Date(filterLocalDateAtMidnight);
                 endOfDay.setHours(23, 59, 59, 999);

                 if (cellValue >= startOfDay && cellValue <= endOfDay) {
                   return 0;
                 }
                 return cellValue < startOfDay ? -1 : 1;
               },
             },
             cellRenderer: (params: any) => {
               const date = params.value;
               if (!date) return  <Box sx={{display:'flex', alignItems:'center', height:'40px'}}>-</Box>;
              return (
               <Box
               sx={{
                 display: "flex",
                 flexDirection: "row" ,
                 justifyContent: "flex-start",
                 alignItems: "center",
                 height: "40px",
                 
               }}
             >
               {formatDateGGMMAAAA(date)}
               </Box>
              );
             },
           },

      {
        headerName: t(
          "screens.fleetAndRoute.gridGroupedVehicles.vehicleStatus"
        ),
        field: "status",
        headerTooltip: t("screens.fleetAndRoute.gridGroupedVehicles.vehicleStatus").toString(),
        tooltipValueGetter: (params: any): string => {
          return params.data?.status === "ACTIVE"
            ? t("screens.fleetAndRoute.status.ACTIVE")
            : t("screens.fleetAndRoute.status.CANCELLED");
        },
      
        // 👇 Mostra il valore tradotto nella cella quando non usi un renderer
        valueFormatter: (params: any) => {
          if (params.value === "ACTIVE") return t("screens.fleetAndRoute.status.ACTIVE");
          if (params.value === "CANCELLED") return t("screens.fleetAndRoute.status.CANCELLED");
          return params.value;
        },
      
        // 👇 Permette al filtro di agGrid di usare il valore tradotto
        filterValueGetter: (params: any) => {
          if (params.data?.status === "ACTIVE") return t("screens.fleetAndRoute.status.ACTIVE");
          if (params.data?.status === "CANCELLED") return t("screens.fleetAndRoute.status.CANCELLED");
          return params.data?.status;
        },
        filter: RegisteredVehicleStatusFilter,
        cellRenderer: (params: any) => {
          return (
            <FormGroup
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <FormControlLabel
                control={
                  <IOSSwitch
                    sx={{ m: 1 }}
                    checked={params.data.status === "ACTIVE"}
                    onChange={() =>
                      handleOpenConfirmDialog(params.data.licensePlate)
                    }
                  />
                }
                label=""
              />
            </FormGroup>
          );
        },
      },
      {
        headerName: t(
          "screens.fleetAndRoute.gridGroupedVehicles.lastTraceabilityCheck"
        ),
        field: "lastTraceabilityCheck",
        minWidth: 200,
        filter: "agDateColumnFilter",
        filterParams: {
          maxNumConditions: 1,
          filterOptions: ['inRange'],
          comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
            if (!cellValue) return -1;
            const startOfDay = new Date(filterLocalDateAtMidnight);
            const endOfDay = new Date(filterLocalDateAtMidnight);
            endOfDay.setHours(23, 59, 59, 999);

            if (cellValue >= startOfDay && cellValue <= endOfDay) {
              return 0;
            }
            return cellValue < startOfDay ? -1 : 1;
          },
        },
        headerTooltip: t("screens.fleetAndRoute.gridGroupedVehicles.lastTraceabilityCheck").toString(),
        tooltipValueGetter: (params: any): string => {
          if (!params.data.lastTraceabilityCheck) return "-";
          return formatDateTime(params.data.lastTraceabilityCheck).toString();
        },
        cellRenderer: (params: any) => {
          if (!params.data.lastTraceabilityCheck) return   <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            alignItems: "center",
            height: "40px",
          }}
        >-</Box>; // Gestione di valori null o undefined

          // Formatta la data
          const formattedDate = formatDateTime(
            params.data.lastTraceabilityCheck
          );
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                alignItems: "center",
                height: "40px",
              }}
            >
              {params.data.traceability === true ? (
                <CheckOutlined
                  sx={{ color: "green", margin: "5px 8px 5px 8px" }}
                />
              ) : (
                  <Block sx={{ color: "#CF0A2C",margin:1 }} />
              )}

              <Typography sx={{ padding: 0, marginTop: "2px" }}>
                {formattedDate}
              </Typography>
            </Box>
          );
        },
      },
    ],
    []
  );

  const [paginationPageSize, setPaginationPageSize] = useState(50);
  const handlePageSizeChange = (newPageSize: number) => {
    setPaginationPageSize(newPageSize);
  };

  const gridRef = useRef<any>(null);
  const onGridReady = () => {
    if (gridRef.current) {
      gridRef.current.api.setDomLayout("autoHeight");
    }
  };
  const localeText = {
    page: t("screens.trips.agGrid.page"),
    nextPage: t("screens.trips.agGrid.nextPage"),
    previousPage: t("screens.trips.agGrid.previousPage"),
    lastPage: t("screens.trips.agGrid.lastPage"),
    firstPage: t("screens.trips.agGrid.firstPage"),
    ariaPaginationPage: t("screens.trips.agGrid.ariaPaginationPage"),
    ariaPagination: t("screens.trips.agGrid.ariaPagination"),
    ariaLabelPagination: t("screens.trips.agGrid.ariaLabelPagination"),
    rangeSelectionEmpty: t("screens.trips.agGrid.rangeSelectionEmpty"),
    paginationSummary: t("screens.trips.agGrid.paginationSummary"),
    empty: t("screens.trips.agGrid.empty"),
    loadingOoo: t("screens.trips.agGrid.loadingOoo"),
    to: t("screens.trips.agGrid.to"),
    of: t("screens.trips.agGrid.of"),
  };

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedLicensePlate, setSelectedLicensePlate] = useState<
    string | null
  >(null);

  const handleOpenConfirmDialog = (licensePlate: string) => {
    setSelectedLicensePlate(licensePlate);
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setSelectedLicensePlate(null);
  };

  const handleConfirmStatusChange = async () => {
    if (selectedLicensePlate) {
      await handleStatusVehicleChange(selectedLicensePlate);
    }
    handleCloseConfirmDialog();
  };







  const [translatedText, setTranslatedText] = useState<string>("");
  const [categoryName,setCategoryName] = useState<string>()
  useEffect(() => {
  
    const displayName = vehiclesGridData?.name ?? vehiclesGridData?.category;
    if (displayName) {
      if (categoryName === "Vehicle") {
        setTranslatedText(
          `${t("screens.dashboard.widgets.registeredFleet.vehicles")} - ${t(`screens.fleetAndRoute.vehicles.${displayName}`)}`
        );
      } else {
        setTranslatedText(
          `${t("screens.dashboard.widgets.registeredFleet.trailers")} - ${t(`screens.fleetAndRoute.vehicles.${displayName}`)}`
        );
      }
    }
  }, [t, vehiclesGridData, categoryName]);
  
  
useEffect(() => {
  if (vehiclesGridData?.vehicles) {
   

    setGridData(vehiclesGridData.vehicles);
  }
}, [vehiclesGridData,categoryName]); // Dipendenza aggiornata

useEffect(() => {
  setCategoryName(vehiclesGridData?.type || vehiclesGridData?.mainCategory
    );
}, [vehiclesGridData?.type]);


  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" sx={{zIndex:99,}}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "24px",
          
        }}
      >
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: "bold",
            textTransform: "uppercase",
          }}
        >
         {translatedText}
        </Typography>
        <IconButton onClick={onClose} sx={{ padding: 0 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Contenuto del Dialog */}
      <DialogContent sx={{ margin: 0, padding: 0 }}>
        <div style={{ width: "100%" }}>
          <div
            className="ag-theme-alpine"
            style={{ height: "100%", width: "100%", overflowX: "auto" }}
          >
            <AgGridReact
               getRowId={(params) => params.data.licensePlate}
              ref={gridRef}
              rowData={gridData} // Row Data for Rows
              columnDefs={columnDefsRegistered} // Column Defs for Columns
              pagination={true}
              paginationPageSize={paginationPageSize}
              defaultColDef={{
                minWidth: 80,
                resizable: false,
                filter: true,
                floatingFilter: true,
                suppressFiltersToolPanel: false,
                sortable: true,
                flex: 1,
              }}
              onGridReady={onGridReady}
              domLayout="autoHeight"
              localeText={localeText}
              enableCellTextSelection={true}  // ✅ Selezione del testo nella cella
              suppressClipboardApi={true} 
            />
            <div style={{ width: "100%", height: "4px" }}>
              <PaginationSizeSelector
                currentPageSize={paginationPageSize}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </div>
        </div>
      </DialogContent>
      <Dialog open={openConfirmDialog} onClose={handleCloseConfirmDialog}>
        <DialogTitle>{t("screens.fleetAndRoute.handleConfirm.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("screens.fleetAndRoute.handleConfirm.text1")}
            <strong>{selectedLicensePlate}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleCloseConfirmDialog}
            sx={{ color: "#333333", borderColor: "#333333" }}
          >
            {t("screens.fleetAndRoute.headerDriver.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmStatusChange}
            sx={{ backgroundColor: "#CF0A2C" }}
            autoFocus
          >
            {t("screens.fleetAndRoute.headerDriver.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default DialogComponent;
